import React, { useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { accountsAPI } from '../../../services/api';
import AddAssetModal from '../../AddAssetModal';
import AccountMatchingModal, {
  AccountMapping,
  PlaidTransactionImportMode,
} from '../../AccountMatchingModal';

interface StepAddAssetProps {
  onNext: (assetAdded: boolean) => void;
  onPlaidComplete?: () => void;
}

const StepAddAsset: React.FC<StepAddAssetProps> = ({ onNext, onPlaidComplete }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [assetSaved, setAssetSaved] = useState(false);

  // Plaid state
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [plaidConnecting, setPlaidConnecting] = useState(false);
  const [plaidConnected, setPlaidConnected] = useState(false);
  const [pendingConnectionId, setPendingConnectionId] = useState<number | null>(null);
  const [pendingAccounts, setPendingAccounts] = useState<any[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [showAccountMatching, setShowAccountMatching] = useState(false);
  const [accountMatchingSuggestions, setAccountMatchingSuggestions] = useState<any[]>([]);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [hasBusinessAccounts, setHasBusinessAccounts] = useState(false);
  const [plaidHistoryNote, setPlaidHistoryNote] = useState<string | null>(null);
  const [latestTransactionByManualAccount, setLatestTransactionByManualAccount] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    accountsAPI.getAccounts({ account_type: 'business' }).then((accounts: any[]) => {
      setHasBusinessAccounts(accounts.length > 0);
    }).catch(() => {});
  }, []);

  const handleConnectBank = async () => {
    try {
      setPlaidConnecting(true);
      const response = await accountsAPI.createLinkToken();
      if (response?.link_token) {
        setLinkToken(response.link_token);
      }
    } catch (error) {
      console.error('Error creating link token:', error);
      setPlaidConnecting(false);
    }
  };

  const onSuccess = async (publicToken: string, metadata: any) => {
    try {
      const response = await accountsAPI.exchangeToken({
        public_token: publicToken,
        institution_name: metadata.institution?.name || 'Unknown Bank'
      });

      if (response.accounts && response.accounts.length > 0) {
        setPendingAccounts(response.accounts);
        setSelectedAccounts(response.accounts.map((acc: any) => acc.account_id));
        setPendingConnectionId(response.connection_id);

        // Get account matching suggestions
        try {
          const suggestions = await accountsAPI.getAccountMatchingSuggestions({
            connection_id: response.connection_id,
            selected_accounts: response.accounts.map((acc: any) => acc.account_id)
          });
          setPlaidHistoryNote(
            typeof suggestions.plaid_history_note === 'string' ? suggestions.plaid_history_note : null
          );
          setLatestTransactionByManualAccount(
            suggestions.latest_transaction_by_manual_account &&
              typeof suggestions.latest_transaction_by_manual_account === 'object'
              ? suggestions.latest_transaction_by_manual_account
              : {}
          );
          if (suggestions.suggestions && suggestions.suggestions.length > 0) {
            setAccountMatchingSuggestions(suggestions.suggestions);
            setShowAccountMatching(true);
          } else {
            await importAccountsWithMappings({}, {
              connId: response.connection_id,
              selAccounts: response.accounts.map((acc: any) => acc.account_id),
            });
          }
        } catch {
          await importAccountsWithMappings({}, {
            connId: response.connection_id,
            selAccounts: response.accounts.map((acc: any) => acc.account_id),
          });
        }
      }

      setLinkToken(null);
    } catch (error) {
      console.error('Error connecting bank:', error);
    } finally {
      setPlaidConnecting(false);
    }
  };

  const importAccountsWithMappings = async (
    mappings: { [plaidAccountId: string]: AccountMapping },
    opts?: {
      transactionImport?: { [plaidAccountId: string]: PlaidTransactionImportMode };
      connId?: number;
      selAccounts?: string[];
    }
  ) => {
    const { transactionImport, connId, selAccounts } = opts ?? {};
    try {
      setMatchingLoading(true);

      const skipped = new Set(
        Object.entries(mappings).filter(([, v]) => v === 'skip').map(([id]) => id)
      );
      const base = selAccounts || selectedAccounts;
      const accountsToImport = base.filter(id => !skipped.has(id));
      const cleanMappings: { [id: string]: number | null } = {};
      const accountTypeOverrides: { [id: string]: 'business' } = {};
      for (const [id, val] of Object.entries(mappings)) {
        if (val === 'skip') continue;
        if (val === 'new_business') {
          cleanMappings[id] = null;
          accountTypeOverrides[id] = 'business';
        } else {
          cleanMappings[id] = val as number | null;
        }
      }

      await accountsAPI.importSelectedAccounts({
        connection_id: connId || pendingConnectionId!,
        selected_accounts: accountsToImport,
        account_mappings: cleanMappings,
        account_type_overrides: accountTypeOverrides,
        transaction_import: transactionImport,
      });

      setShowAccountMatching(false);
      setPendingAccounts([]);
      setSelectedAccounts([]);
      setPendingConnectionId(null);
      setAccountMatchingSuggestions([]);
      setPlaidHistoryNote(null);
      setLatestTransactionByManualAccount({});
      setPlaidConnected(true);

      // On successful Plaid connection during onboarding, complete immediately
      if (onPlaidComplete) {
        onPlaidComplete();
      }
    } catch (error) {
      console.error('Error importing accounts:', error);
    } finally {
      setMatchingLoading(false);
    }
  };

  const onExit = () => {
    setLinkToken(null);
    setPlaidConnecting(false);
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Auto-open Plaid Link when token is ready
  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  return (
    <div className="flex flex-col">
      <h1 className="text-3xl font-bold text-white mb-2">Add your first asset</h1>
      <p className="text-base text-white/50 mb-8">This gives you your starting net worth number.</p>

      {/* Primary CTA: Connect bank via Plaid */}
      <div className="rounded-xl border border-purple-500/30 bg-purple-500/5 p-5 mb-4">
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl">🏦</span>
          <div>
            <h3 className="text-base font-semibold text-white">Connect your bank with Plaid</h3>
            <p className="text-sm text-white/50 mt-1">
              Automatically import your accounts, balances, and 90 days of transactions. Securely powered by Plaid.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleConnectBank}
          disabled={plaidConnecting}
          className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors w-full"
        >
          {plaidConnecting ? 'Connecting...' : 'Connect your bank'}
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-sm text-white/30">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Secondary: Add manually */}
      <AddAssetModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        isOnboarding
        onSuccess={() => { setAssetSaved(true); setModalOpen(false); }}
      />

      {assetSaved ? (
        <>
          <p className="text-sm text-green-400 mb-4">Asset added.</p>
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-6 py-3 font-medium text-base transition-colors"
            >
              Add another asset
            </button>
            <button
              type="button"
              onClick={() => onNext(true)}
              className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-6 py-3 font-medium text-base transition-colors"
            >
              Next step
            </button>
          </div>
          <p className="text-sm text-white/50">You can add more assets any time from the Assets page.</p>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-6 py-3 font-medium text-base transition-colors self-start"
          >
            Add accounts manually
          </button>
          <button
            type="button"
            onClick={() => onNext(false)}
            className="text-base text-white/40 hover:text-white transition-colors mt-4 self-start"
          >
            Skip for now
          </button>
          <p className="text-sm text-white/50 mt-2">You can add assets any time from the Assets page.</p>
        </>
      )}

      {/* Account Matching Modal */}
      <AccountMatchingModal
        isOpen={showAccountMatching}
        onClose={() => {
          setShowAccountMatching(false);
          setAccountMatchingSuggestions([]);
          setPlaidHistoryNote(null);
          setLatestTransactionByManualAccount({});
        }}
        suggestions={accountMatchingSuggestions}
        onConfirm={(m, t) => importAccountsWithMappings(m, { transactionImport: t })}
        loading={matchingLoading}
        hasBusinessAccounts={hasBusinessAccounts}
        plaidHistoryNote={plaidHistoryNote}
        latestTransactionByManualAccount={latestTransactionByManualAccount}
      />
    </div>
  );
};

export default StepAddAsset;
